import { InternalLinkReplacements } from '@boxcryptor-web/ui';
import React, { useContext } from 'react';
import urljoin from 'url-join';
import { Currency } from './currency';
import { Language } from './language';
import { Page } from './pages/Page';

export const SITEMAP_SPLAT = '/sitemap.xml';

export type Theme = 'blue' | 'green';

export type LinkedUrls = {
  translations: Array<{ lang: Language; url: string }>;
  defaultUrl?: string;
  originalUrl?: string;
};

export type PageContext = {
  theme: Theme;
  linkedUrls: LinkedUrls;
  lang: Language;
  url: string;
  sitemapUrl?: string;
  rewrittenPath?: string;
  legacyContentSlug: string;
  baseUrl: string;
};

export function mapPageToPageContext(page: Page, siteUrl: string): PageContext {
  const linkedUrls = page.getLinkedUrls(siteUrl);
  const lang = page.getLang();
  const url = urljoin(siteUrl, page.getUrlSplat());
  const sitemapUrl = urljoin(siteUrl, SITEMAP_SPLAT);
  const rewrittenPath = page.getFileSplat() !== page.getUrlSplat() ? page.getUrlSplat() : undefined;
  const legacyContentSlug = page.getContentSlug();
  const theme = page.getTheme();
  return { linkedUrls, lang, url, sitemapUrl, rewrittenPath, legacyContentSlug, theme, baseUrl: siteUrl };
}

const PageContextContext = React.createContext<PageContext | undefined>(undefined);
export const usePageContext = () => {
  const ctx = useContext(PageContextContext);
  if (ctx === undefined) {
    throw new Error(`Provider missing for PageContextContext`);
  }
  return ctx;
};
export const PageContextContextProvider = PageContextContext.Provider;

export type PaginationContext = {
  currentPageNumber: number;
  firstPageNumber: number;
  lastPageNumber: number;
  links: Record<number, string>;
};

const PaginationContextContext = React.createContext<PaginationContext | undefined>(undefined);
export const useOptionalPaginationContext = () => useContext(PaginationContextContext);
export const usePaginationContext = () => {
  const ctx = useContext(PaginationContextContext);
  if (ctx === undefined) {
    throw new Error(`Provider missing for PaginationContextContext`);
  }
  return ctx;
};
export const PaginationContextContextProvider = PaginationContextContext.Provider;

export type LinkReplacements = InternalLinkReplacements;

export type GatsbyPageLinkContext = {
  internalLinkReplacements: LinkReplacements;
  allInternalLinks: string[];
  baseUrl: string;
};

const CurrencyContext = React.createContext<Currency | undefined>(undefined);

export const useCurrency: () => Currency = () => {
  const ctx = useContext(CurrencyContext);
  if (ctx === undefined) {
    throw new Error(`useCurrency can only be used within a CurrencyPage.`);
  }
  return ctx;
};

export const CurrencyProvider = CurrencyContext.Provider;
